import { useEffect, useRef } from 'react';

/**
 * Mantém a tela acesa (impede o modo descanso) enquanto o componente que chama
 * este hook estiver montado. Usa a Screen Wake Lock API (`navigator.wakeLock`).
 *
 * Comportamento:
 *  - Pede o bloqueio de tela ao montar e sempre que o app volta a ficar visível,
 *    porque o SO libera o bloqueio automaticamente quando a aba/PWA fica oculta
 *    (ex.: troca de app, minimiza, bloqueia o aparelho).
 *  - Libera o bloqueio ao desmontar (logout/sair da sessão), devolvendo o
 *    comportamento normal de economia de bateria.
 *  - Em navegadores sem suporte (ex.: iOS < 16.4), falha silenciosamente — não
 *    quebra o app nem lança erro.
 *
 * Requisitos: HTTPS (PWAs já atendem) e documento ativo. Não exige prompt de
 * permissão do usuário.
 */
export const useWakeLock = () => {
  const lockRef = useRef(null);

  useEffect(() => {
    const isSupported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
    if (!isSupported) return undefined;

    const request = async () => {
      try {
        // Só solicita quando visível; bloqueio oculto é descartado pelo SO.
        if (document.visibilityState === 'visible') {
          lockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (_) {
        /* Usuário/sistema negou ou recurso indisponível no momento — ignora. */
      }
    };

    const onVisibilityChange = () => {
      if (lockRef.current && document.visibilityState === 'visible') {
        request();
      }
    };

    request();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (lockRef.current) {
        lockRef.current.release().catch(() => {});
        lockRef.current = null;
      }
    };
  }, []);
};

export default useWakeLock;
